/**
 * Export style configuration types.
 * Used by both the frontend canvas preview and PPTX/PDF generators.
 */

/** CSS-friendly style for frontend canvas rendering */
export interface ExportStyle {
  id: string;
  scope: string;
  label: string;

  // Colors (hex with '#' prefix for CSS)
  colorPrimary: string;
  colorSecondary: string;
  colorAccent: string;
  colorBodyBg: string;
  colorText: string;
  colorTextMuted: string;
  colorFooterText: string;
  titleBgType: 'gradient' | 'image' | 'solid';
  titleBgColor?: string;
  titleTextColor: string;

  // Fonts
  fontPrimary: string;
  fontCss?: string;

  // Chart colors (hex with '#' prefix)
  chartColors?: string[];

  // Logo URLs (public Firebase Storage URLs)
  logoMainUrl?: string;
  logoHeaderUrl?: string;
  titleBgImageUrl?: string;
  coverImageUrl?: string;
  /** The Key Accounts dashboard skin's mark, from `decorations.dashboard.logoMarkPath`. */
  dashboardMarkUrl?: string;

  // Extensible decorations
  decorations: Record<string, unknown>;

  isActive: boolean;
}

/** DB row shape from ST_DashAI_export_styles */
export interface ExportStyleRow {
  id: string;
  scope: string;
  workspaces: string[];
  label: string;
  color_primary: string;
  color_secondary: string;
  color_accent: string;
  color_body_bg: string;
  color_text: string;
  color_text_muted: string;
  color_footer_text: string;
  title_bg_type: 'gradient' | 'image' | 'solid';
  title_bg_color: string | null;
  title_text_color: string;
  font_primary: string;
  font_css: string | null;
  logo_main_path: string | null;
  logo_header_path: string | null;
  title_bg_image_path: string | null;
  chart_colors: string[] | null;
  cover_image_path: string | null;
  decorations: Record<string, unknown>;
  is_active: boolean;
  updated_at: string;
  created_at: string;
}

/** Convert DB row to CSS-friendly ExportStyle */
export function rowToExportStyle(row: ExportStyleRow, storageBucket?: string): ExportStyle {
  const toUrl = (path: string | null) => {
    if (!path || !storageBucket) return undefined;
    // Firebase Storage public URL. Path segments are encoded
    // (filenames can contain spaces / parentheses).
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    return `https://storage.googleapis.com/${storageBucket}/${encoded}`;
  };

  const skin = row.decorations?.dashboard as { logoMarkPath?: unknown } | undefined;
  const markPath = typeof skin?.logoMarkPath === 'string' ? skin.logoMarkPath : null;

  return {
    id: row.id,
    scope: row.scope,
    label: row.label,
    colorPrimary: `#${row.color_primary}`,
    colorSecondary: `#${row.color_secondary}`,
    colorAccent: `#${row.color_accent}`,
    colorBodyBg: `#${row.color_body_bg}`,
    colorText: `#${row.color_text}`,
    colorTextMuted: `#${row.color_text_muted}`,
    colorFooterText: `#${row.color_footer_text}`,
    titleBgType: row.title_bg_type,
    titleBgColor: row.title_bg_color ? `#${row.title_bg_color}` : undefined,
    titleTextColor: `#${row.title_text_color}`,
    fontPrimary: row.font_primary,
    fontCss: row.font_css ?? undefined,
    chartColors: row.chart_colors?.map(c => `#${c}`) ?? undefined,
    logoMainUrl: toUrl(row.logo_main_path),
    logoHeaderUrl: toUrl(row.logo_header_path),
    titleBgImageUrl: toUrl(row.title_bg_image_path),
    coverImageUrl: toUrl(row.cover_image_path),
    dashboardMarkUrl: toUrl(markPath),
    decorations: row.decorations || {},
    isActive: row.is_active,
  };
}
